import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldAlert, Trash2, UserMinus, Users, Layers,
  Loader2, AlertTriangle, ChevronDown, Search, CalendarRange, ScrollText, Building2,
  Clock, Save,
} from 'lucide-react';
import { Spinner, Modal } from '@/components/shared/UIComponents';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchTenants, removeUser, deleteTenantFull,
  fetchTenantUsers,
  fetchPlans, deletePlanFull, countTenantsOnPlan,
  fetchAuditLogByDateRange, deleteAuditLogsByIds,
  fetchCronSchedule, updateCronSchedule, fetchSyncLog,
} from '@/lib/api';
import type { Tenant, TenantPlan, AuditLog } from '@/types';
import type { AppTenantUser, SyncLogEntry } from '@/lib/api';
import { formatScheduleConversions } from '@/lib/timeFormat';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function utcToLocalTimeString(hour: number, minute: number): string {
  const d = new Date();
  d.setUTCHours(hour, minute, 0, 0);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function SupportPage() {
  const { isSuperAdmin } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [users, setUsers] = useState<AppTenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Remove user modal
  const [removeTarget, setRemoveTarget] = useState<AppTenantUser | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // Delete tenant modal
  const [showDeleteTenant, setShowDeleteTenant] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Plans state
  const [plans, setPlans] = useState<TenantPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [showDeletePlan, setShowDeletePlan] = useState(false);
  const [planDeleteConfirm, setPlanDeleteConfirm] = useState('');
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [deletePlanError, setDeletePlanError] = useState<string | null>(null);
  const [planTenantCount, setPlanTenantCount] = useState<number>(0);
  const [loadingPlanCount, setLoadingPlanCount] = useState(false);

  // Cron schedule state
  const [scheduleHour, setScheduleHour] = useState(2);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [cronLoading, setCronLoading] = useState(true);
  const [cronSaving, setCronSaving] = useState(false);
  const [cronSuccess, setCronSuccess] = useState<string | null>(null);
  const [cronError, setCronError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<SyncLogEntry | null>(null);
  const [currentCron, setCurrentCron] = useState('0 2 * * *');

  const selectedTenant = tenants.find(t => t.id === selectedTenantId);
  const selectedPlan = plans.find(p => p.id === selectedPlanId);

  // ── Load cron schedule on mount ─────────────────────────────────────────
  useEffect(() => {
    fetchCronSchedule().then(data => {
      setCurrentCron(data.schedule);
      const parts = data.schedule.split(' ');
      setScheduleMinute(parseInt(parts[0]) || 0);
      setScheduleHour(parseInt(parts[1]) || 0);
    }).catch(() => {
      setCronError('Could not load schedule');
    }).finally(() => setCronLoading(false));

    fetchSyncLog(1).then(logs => {
      if (logs.length > 0) setLastSync(logs[0]);
    }).catch(() => {});
  }, []);

  const handleSaveSchedule = async () => {
    setCronSaving(true);
    setCronError(null);
    setCronSuccess(null);
    try {
      const newCron = `${scheduleMinute} ${scheduleHour} * * *`;
      await updateCronSchedule(newCron);
      setCurrentCron(newCron);
      setCronSuccess(`Schedule updated to ${String(scheduleHour).padStart(2, '0')}:${String(scheduleMinute).padStart(2, '0')} UTC`);
      setTimeout(() => setCronSuccess(null), 5000);
    } catch (err: any) {
      setCronError(err?.message || 'Failed to update schedule');
    } finally {
      setCronSaving(false);
    }
  };

  // ── Load tenants on mount ─────────────────────────────────────────────
  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const [tenantData, planData] = await Promise.all([fetchTenants(), fetchPlans()]);
      setTenants(tenantData);
      setPlans(planData);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  // ── Load users when tenant changes ────────────────────────────────────
  const loadUsers = useCallback(async (tenantId: string) => {
    if (!tenantId) { setUsers([]); return; }
    setLoadingUsers(true);
    try {
      const data = await fetchTenantUsers(tenantId);
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(selectedTenantId);
  }, [selectedTenantId, loadUsers]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleRemoveUser = async () => {
    if (!removeTarget || !selectedTenantId) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeUser(selectedTenantId, removeTarget.user_id);
      setRemoveTarget(null);
      await loadUsers(selectedTenantId);
    } catch (err) {
      console.error('Failed to remove user:', err);
      setRemoveError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setRemoving(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!selectedTenantId || !selectedTenant) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteTenantFull(selectedTenantId);
      setSelectedTenantId('');
      setUsers([]);
      setShowDeleteTenant(false);
      setDeleteConfirmText('');
      await loadTenants();
    } catch (err) {
      console.error('Failed to delete tenant:', err);
      setDeleteError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Plan handlers ────────────────────────────────────────────────────
  const handleSelectPlan = async (planId: string) => {
    setSelectedPlanId(planId);
    setPlanTenantCount(0);
    if (!planId) return;
    setLoadingPlanCount(true);
    try {
      const count = await countTenantsOnPlan(planId);
      setPlanTenantCount(count);
    } catch {
      setPlanTenantCount(0);
    } finally {
      setLoadingPlanCount(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlanId || !selectedPlan) return;
    setDeletingPlan(true);
    setDeletePlanError(null);
    try {
      await deletePlanFull(selectedPlanId);
      setSelectedPlanId('');
      setShowDeletePlan(false);
      setPlanDeleteConfirm('');
      await loadTenants(); // refresh both tenants and plans
    } catch (err) {
      console.error('Failed to delete plan:', err);
      setDeletePlanError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setDeletingPlan(false);
    }
  };

  // ── Bulk Cleanup state ───────────────────────────────────────────────
  type CleanupType = 'tenants' | 'plans' | 'audit';
  const [cleanupType, setCleanupType] = useState<CleanupType>('tenants');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [auditResults, setAuditResults] = useState<AuditLog[]>([]);
  const [cleanupSearched, setCleanupSearched] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [selectedCleanupIds, setSelectedCleanupIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
  const [bulkDeleteSuccess, setBulkDeleteSuccess] = useState<string | null>(null);

  // Filter tenants/plans client-side by created_at within date range
  const filteredTenants = useMemo(() => {
    if (!cleanupSearched || !dateFrom || !dateTo) return [];
    return tenants.filter(t => {
      const d = t.created_at?.slice(0, 10);
      return d && d >= dateFrom && d <= dateTo;
    });
  }, [tenants, dateFrom, dateTo, cleanupSearched]);

  const filteredPlans = useMemo(() => {
    if (!cleanupSearched || !dateFrom || !dateTo) return [];
    return plans.filter(p => {
      const d = p.created_at?.slice(0, 10);
      return d && d >= dateFrom && d <= dateTo;
    });
  }, [plans, dateFrom, dateTo, cleanupSearched]);

  const cleanupResults = cleanupType === 'tenants' ? filteredTenants
    : cleanupType === 'plans' ? filteredPlans
    : auditResults;

  const handleCleanupSearch = async () => {
    if (!dateFrom || !dateTo) return;
    setCleanupSearched(true);
    setSelectedCleanupIds(new Set());
    setBulkDeleteSuccess(null);
    if (cleanupType === 'audit') {
      setCleanupLoading(true);
      try {
        const data = await fetchAuditLogByDateRange(dateFrom, dateTo);
        setAuditResults(data);
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
        setAuditResults([]);
      } finally {
        setCleanupLoading(false);
      }
    }
  };

  const toggleCleanupId = (id: string) => {
    setSelectedCleanupIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllCleanup = () => {
    if (selectedCleanupIds.size === cleanupResults.length) {
      setSelectedCleanupIds(new Set());
    } else {
      setSelectedCleanupIds(new Set(cleanupResults.map((r: any) => r.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCleanupIds.size === 0) return;
    setBulkDeleting(true);
    setBulkDeleteError(null);
    const count = selectedCleanupIds.size;
    try {
      if (cleanupType === 'tenants') {
        for (const id of selectedCleanupIds) {
          await deleteTenantFull(id);
        }
      } else if (cleanupType === 'plans') {
        for (const id of selectedCleanupIds) {
          await deletePlanFull(id);
        }
      } else {
        await deleteAuditLogsByIds([...selectedCleanupIds]);
      }
      // Refresh
      setShowBulkDeleteModal(false);
      setBulkDeleteConfirm('');
      setSelectedCleanupIds(new Set());
      setBulkDeleteSuccess(`Successfully deleted ${count} ${cleanupType === 'audit' ? 'audit log entries' : cleanupType}.`);
      if (cleanupType === 'tenants' || cleanupType === 'plans') {
        await loadTenants();
      }
      if (cleanupType === 'audit') {
        setAuditResults(prev => prev.filter(a => !selectedCleanupIds.has(a.id)));
      }
    } catch (err) {
      console.error('Bulk delete failed:', err);
      setBulkDeleteError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Guard ─────────────────────────────────────────────────────────────
  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <ShieldAlert size={48} className="mx-auto mb-4 text-red-600 dark:text-red-400" />
          <h2 className="text-xl font-semibold text-surface-900">Access Denied</h2>
          <p className="text-sm text-surface-500 mt-1">
            This page is restricted to Super Admins only.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Support Tools</h1>
        <p className="text-sm text-surface-500 mt-1">
          Manage user and tenant data. All actions are permanent and irreversible.
        </p>
      </div>

      {/* Warning banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
        <AlertTriangle size={20} className="text-red-500 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-red-600">Danger Zone</p>
          <p className="text-xs text-red-500/80 mt-0.5">
            Actions on this page permanently delete data from both databases.
            Stripe subscriptions are NOT automatically cancelled — handle those manually.
          </p>
        </div>
      </div>

      {/* ── Sync Schedule ────────────────────────────────────────────────── */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-1">
          <Clock size={16} className="text-brand-500" />
          <h3 className="text-sm font-semibold text-surface-900">Sync Schedule</h3>
        </div>
        <p className="text-xs text-surface-500 mb-4">Daily usage metrics sync cron job</p>

        {cronLoading ? (
          <div className="flex items-center gap-2 py-4">
            <Spinner size="sm" />
            <span className="text-sm text-surface-500">Loading schedule...</span>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-4 mb-3">
              {/* Hour picker */}
              <div>
                <label className="block text-xs text-surface-500 mb-1">UTC Hour</label>
                <select
                  value={scheduleHour}
                  onChange={e => setScheduleHour(parseInt(e.target.value))}
                  className="input-field py-2 text-sm w-20 appearance-none"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>

              <span className="text-lg font-bold text-surface-400 pb-2">:</span>

              {/* Minute picker */}
              <div>
                <label className="block text-xs text-surface-500 mb-1">Minute</label>
                <select
                  value={scheduleMinute}
                  onChange={e => setScheduleMinute(parseInt(e.target.value))}
                  className="input-field py-2 text-sm w-20 appearance-none"
                >
                  {[0, 15, 30, 45].map(m => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleSaveSchedule}
                disabled={cronSaving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {cronSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {cronSaving ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>

            {/* Time info */}
            <div className="space-y-1 text-xs text-surface-500">
              <p>
                <span className="text-surface-400 w-20 inline-block">Local time:</span>
                <span className="font-medium text-surface-700">
                  {utcToLocalTimeString(scheduleHour, scheduleMinute)}
                </span>
              </p>
              {/* US Eastern + India IST translations. The picker stores
                  UTC, but most operators think in their local zone. The
                  helper recomputes live as scheduleHour / scheduleMinute
                  change. */}
              <p>
                <span className="text-surface-400 w-20 inline-block">US / IN:</span>
                <span className="font-medium text-surface-700">
                  {formatScheduleConversions(scheduleHour, scheduleMinute)}
                </span>
              </p>
              <p>
                <span className="text-surface-400 w-20 inline-block">Cron:</span>
                <code className="font-mono text-surface-600 bg-surface-100 dark:bg-surface-800 px-1.5 py-0.5 rounded">
                  {currentCron}
                </code>
                <span className="ml-1.5 text-surface-400">(daily)</span>
              </p>
              {lastSync && (
                <p>
                  <span className="text-surface-400 w-20 inline-block">Last sync:</span>
                  <span className="font-medium text-surface-700">{formatRelativeTime(lastSync.created_at)}</span>
                  <span className="ml-1.5">·</span>
                  <span className={`ml-1.5 ${
                    lastSync.status === 'success' ? 'text-emerald-600' :
                    lastSync.status === 'partial' ? 'text-amber-600' : 'text-red-600'
                  }`}>{lastSync.status}</span>
                  <span className="ml-1.5">·</span>
                  <span className="ml-1.5 text-surface-400">{lastSync.triggered_by}</span>
                </p>
              )}
            </div>

            {/* Success / Error feedback */}
            {cronSuccess && (
              <div className="mt-3 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-xs text-emerald-600">
                {cronSuccess}
              </div>
            )}
            {cronError && (
              <div className="mt-3 p-2.5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-xs text-red-600">
                {cronError}
              </div>
            )}
          </>
        )}
      </div>

      {/* Tenant Selector */}
      <div className="glass-panel p-5">
        <label className="block text-sm font-medium text-surface-700 mb-2">
          Select Tenant
        </label>
        <div className="relative">
          <select
            value={selectedTenantId}
            onChange={e => setSelectedTenantId(e.target.value)}
            className="input-field w-full max-w-md py-2.5 pr-10 text-sm appearance-none"
          >
            <option value="">-- Choose a tenant --</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug}) — {t.status}
              </option>
            ))}
          </select>
          <ChevronDown size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 pointer-events-none" />
        </div>
      </div>

      {/* Selected tenant content */}
      {selectedTenantId && selectedTenant && (
        <>
          {/* Users Table */}
          <div className="glass-panel overflow-hidden">
            <div className="px-5 py-4 border-b border-surface-300 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-surface-400" />
                <h3 className="font-semibold text-surface-900">
                  Users in {selectedTenant.name}
                </h3>
                <span className="text-xs text-surface-500 ml-1">
                  ({users.length} total)
                </span>
              </div>
            </div>

            {loadingUsers ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="md" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-surface-500">
                <Users size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No users found for this tenant</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-300/60 text-left text-surface-500">
                      <th className="px-5 py-3 font-medium">User</th>
                      <th className="px-5 py-3 font-medium">Role</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium">Plan</th>
                      <th className="px-5 py-3 font-medium">Joined</th>
                      <th className="px-5 py-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id}
                        className="border-b border-surface-300/30 hover:bg-surface-100/50">
                        <td className="px-5 py-3">
                          <div className="font-medium text-surface-900">
                            {u.full_name || '(no name)'}
                          </div>
                          <div className="text-xs text-surface-500">{u.email}</div>
                        </td>
                        <td className="px-5 py-3 capitalize text-surface-700">{u.role}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            u.status === 'blocked'
                              ? 'badge-red'
                              : 'bg-green-900/30 text-green-400'
                          }`}>{u.status || 'active'}</span>
                        </td>
                        <td className="px-5 py-3 text-surface-500 capitalize">{u.plan_name || '—'}</td>
                        <td className="px-5 py-3 text-surface-500">
                          {u.joined_at ? formatDate(u.joined_at) : '—'}
                        </td>
                        <td className="px-5 py-3">
                          <button
                            onClick={() => { setRemoveTarget(u); setRemoveError(null); }}
                            className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1"
                          >
                            <UserMinus size={12} /> Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Delete Tenant */}
          <div className="glass-panel p-5 border-red-800/40">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-surface-900 flex items-center gap-2">
                  <Trash2 size={16} className="text-red-500" />
                  Delete Tenant Entirely
                </h3>
                <p className="text-xs text-surface-500 mt-1">
                  Permanently removes ALL data from both databases. This cannot be undone.
                </p>
              </div>
              <button
                onClick={() => { setShowDeleteTenant(true); setDeleteError(null); setDeleteConfirmText(''); }}
                className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
              >
                Delete Tenant
              </button>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ── Delete Plan Section ────────────────────────────────────────────── */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-4">
          <Layers size={16} className="text-red-600 dark:text-red-400" />
          <h3 className="font-semibold text-surface-900">Delete Plan (Cascade)</h3>
        </div>
        <p className="text-xs text-surface-500 mb-3">
          Permanently delete a B2B plan and <strong>ALL tenants assigned to it</strong> (and all their data).
          This is irreversible.
        </p>

        <div className="flex items-end gap-3">
          <div className="flex-1 max-w-md">
            <label className="block text-xs font-medium text-surface-600 mb-1.5">Select Plan</label>
            <div className="relative">
              <select
                value={selectedPlanId}
                onChange={e => handleSelectPlan(e.target.value)}
                className="input-field w-full py-2.5 pr-10 text-sm appearance-none"
              >
                <option value="">-- Choose a plan --</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.display_name} ({p.name}) — {p.is_active ? 'Active' : 'Archived'}
                  </option>
                ))}
              </select>
              <ChevronDown size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 pointer-events-none" />
            </div>
          </div>

          {selectedPlanId && selectedPlan && (
            <button
              onClick={() => { setShowDeletePlan(true); setDeletePlanError(null); setPlanDeleteConfirm(''); }}
              className="px-4 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors shrink-0"
            >
              Delete Plan
            </button>
          )}
        </div>

        {/* Impact preview */}
        {selectedPlanId && selectedPlan && (
          <div className="mt-4 p-3 rounded-lg bg-surface-100/50 border border-overlay/[0.06]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: selectedPlan.color }} />
              <span className="text-sm font-medium text-surface-900">{selectedPlan.display_name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${selectedPlan.is_active ? 'badge-emerald' : 'bg-surface-200 text-surface-500'}`}>
                {selectedPlan.is_active ? 'Active' : 'Archived'}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-surface-500">Tenants affected:</span>{' '}
                {loadingPlanCount ? (
                  <Loader2 size={12} className="inline animate-spin text-surface-400" />
                ) : (
                  <span className={`font-medium ${planTenantCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-surface-900'}`}>
                    {planTenantCount}
                  </span>
                )}
              </div>
              <div>
                <span className="text-surface-500">Max users:</span>{' '}
                <span className="font-medium text-surface-900">{selectedPlan.max_users ?? '∞'}</span>
              </div>
              <div>
                <span className="text-surface-500">Storage:</span>{' '}
                <span className="font-medium text-surface-900">{selectedPlan.max_storage_mb} MB</span>
              </div>
              <div>
                <span className="text-surface-500">Price:</span>{' '}
                <span className="font-medium text-surface-900">
                  {selectedPlan.price_per_seat_monthly > 0
                    ? `$${(selectedPlan.price_per_seat_monthly / 100).toFixed(2)}/seat/mo`
                    : 'Free'}
                </span>
              </div>
            </div>
            {planTenantCount > 0 && (
              <div className="mt-2 flex items-start gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-xs text-red-500">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  <strong>{planTenantCount} tenant{planTenantCount !== 1 ? 's' : ''}</strong> will be permanently
                  deleted along with ALL their users, trips, expenses, configs, and App DB data.
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ── Bulk Cleanup by Date Range ──────────────────────────────────── */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-1">
          <CalendarRange size={16} className="text-red-600 dark:text-red-400" />
          <h3 className="font-semibold text-surface-900">Bulk Cleanup by Date Range</h3>
        </div>
        <p className="text-xs text-surface-500 mb-4">
          Find and delete tenants, plans, or audit logs created within a date range.
        </p>

        {/* Type selector pills */}
        <div className="flex gap-1 mb-4">
          {([
            { key: 'tenants' as CleanupType, label: 'Tenants', icon: Building2 },
            { key: 'plans' as CleanupType, label: 'Global Plans', icon: Layers },
            { key: 'audit' as CleanupType, label: 'Audit Logs', icon: ScrollText },
          ]).map(tab => (
            <button
              key={tab.key}
              onClick={() => { setCleanupType(tab.key); setCleanupSearched(false); setSelectedCleanupIds(new Set()); setAuditResults([]); setBulkDeleteSuccess(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                cleanupType === tab.key
                  ? 'bg-red-500/15 text-red-500 border border-red-500/30'
                  : 'bg-surface-100 text-surface-500 border border-transparent hover:bg-surface-200'
              }`}
            >
              <tab.icon size={13} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Date range inputs + Search button */}
        <div className="flex items-end gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setCleanupSearched(false); }}
              className="input-field py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setCleanupSearched(false); }}
              className="input-field py-2 text-sm"
            />
          </div>
          <button
            onClick={handleCleanupSearch}
            disabled={!dateFrom || !dateTo || cleanupLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-surface-900 text-white text-sm font-medium hover:bg-surface-800 transition-colors disabled:opacity-50"
          >
            {cleanupLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Search
          </button>
        </div>

        {/* Success banner */}
        {bulkDeleteSuccess && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-sm text-emerald-600 flex items-center gap-2">
            <span>{bulkDeleteSuccess}</span>
          </div>
        )}

        {/* Results table */}
        {cleanupSearched && !cleanupLoading && (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-surface-500">
                {cleanupResults.length} result{cleanupResults.length !== 1 ? 's' : ''} found
              </span>
              {selectedCleanupIds.size > 0 && (
                <button
                  onClick={() => { setShowBulkDeleteModal(true); setBulkDeleteError(null); setBulkDeleteConfirm(''); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors"
                >
                  <Trash2 size={12} />
                  Delete {selectedCleanupIds.size} Selected
                </button>
              )}
            </div>

            {cleanupResults.length === 0 ? (
              <div className="text-center py-8 text-surface-500">
                <Search size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No {cleanupType === 'audit' ? 'audit logs' : cleanupType} found in this date range.</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-overlay/[0.06]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-300/60 text-left text-surface-500 bg-surface-50/50">
                      <th className="px-3 py-2.5 w-8">
                        <input
                          type="checkbox"
                          checked={selectedCleanupIds.size === cleanupResults.length && cleanupResults.length > 0}
                          onChange={toggleAllCleanup}
                          className="rounded border-surface-300"
                        />
                      </th>
                      {cleanupType === 'tenants' && (
                        <>
                          <th className="px-3 py-2.5 font-medium">Name</th>
                          <th className="px-3 py-2.5 font-medium">Slug</th>
                          <th className="px-3 py-2.5 font-medium">Status</th>
                          <th className="px-3 py-2.5 font-medium">Plan</th>
                          <th className="px-3 py-2.5 font-medium">Users</th>
                          <th className="px-3 py-2.5 font-medium">Created</th>
                        </>
                      )}
                      {cleanupType === 'plans' && (
                        <>
                          <th className="px-3 py-2.5 font-medium">Display Name</th>
                          <th className="px-3 py-2.5 font-medium">Name</th>
                          <th className="px-3 py-2.5 font-medium">Status</th>
                          <th className="px-3 py-2.5 font-medium">Tenants</th>
                          <th className="px-3 py-2.5 font-medium">Created</th>
                        </>
                      )}
                      {cleanupType === 'audit' && (
                        <>
                          <th className="px-3 py-2.5 font-medium">Action</th>
                          <th className="px-3 py-2.5 font-medium">Resource Type</th>
                          <th className="px-3 py-2.5 font-medium">Resource ID</th>
                          <th className="px-3 py-2.5 font-medium">Metadata</th>
                          <th className="px-3 py-2.5 font-medium">Created</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {cleanupType === 'tenants' && (filteredTenants as Tenant[]).map(t => (
                      <tr key={t.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={selectedCleanupIds.has(t.id)} onChange={() => toggleCleanupId(t.id)} className="rounded border-surface-300" />
                        </td>
                        <td className="px-3 py-2.5 font-medium text-surface-900">{t.name}</td>
                        <td className="px-3 py-2.5 text-surface-500 font-mono text-xs">{t.slug}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            t.status === 'active' ? 'badge-emerald' : 'badge-red'
                          }`}>{t.status}</span>
                        </td>
                        <td className="px-3 py-2.5 text-surface-500 text-xs">{(t.plan as TenantPlan)?.display_name || '—'}</td>
                        <td className="px-3 py-2.5 text-surface-500">{t.current_user_count}</td>
                        <td className="px-3 py-2.5 text-surface-500 text-xs">{t.created_at ? formatDate(t.created_at) : '—'}</td>
                      </tr>
                    ))}
                    {cleanupType === 'plans' && (filteredPlans as TenantPlan[]).map(p => (
                      <tr key={p.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={selectedCleanupIds.has(p.id)} onChange={() => toggleCleanupId(p.id)} className="rounded border-surface-300" />
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: p.color }} />
                            <span className="font-medium text-surface-900">{p.display_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-surface-500 font-mono text-xs">{p.name}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            p.is_active ? 'badge-emerald' : 'bg-surface-200 text-surface-500'
                          }`}>{p.is_active ? 'Active' : 'Archived'}</span>
                        </td>
                        <td className="px-3 py-2.5 text-surface-500">{tenants.filter(t => t.plan_id === p.id).length}</td>
                        <td className="px-3 py-2.5 text-surface-500 text-xs">{p.created_at ? formatDate(p.created_at) : '—'}</td>
                      </tr>
                    ))}
                    {cleanupType === 'audit' && (auditResults as AuditLog[]).map(a => (
                      <tr key={a.id} className="border-b border-surface-300/30 hover:bg-surface-100/50">
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={selectedCleanupIds.has(a.id)} onChange={() => toggleCleanupId(a.id)} className="rounded border-surface-300" />
                        </td>
                        <td className="px-3 py-2.5 font-medium text-surface-900 text-xs">{a.action}</td>
                        <td className="px-3 py-2.5 text-surface-500 text-xs">{a.resource_type}</td>
                        <td className="px-3 py-2.5 text-surface-500 font-mono text-[10px]">{a.resource_id ? a.resource_id.slice(0, 8) + '...' : '—'}</td>
                        <td className="px-3 py-2.5 text-surface-500 text-[10px] max-w-[200px] truncate">
                          {Object.keys(a.metadata).length > 0 ? JSON.stringify(a.metadata).slice(0, 60) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-surface-500 text-xs">{formatDate(a.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Bulk Delete Confirmation Modal ───────────────────────────────── */}
      <Modal
        open={showBulkDeleteModal}
        onClose={() => { setShowBulkDeleteModal(false); setBulkDeleteConfirm(''); }}
        title={`Delete ${selectedCleanupIds.size} ${cleanupType === 'audit' ? 'Audit Log Entries' : cleanupType === 'tenants' ? 'Tenants' : 'Plans'}`}
        subtitle="This is an extremely destructive action."
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
            <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div className="text-sm text-red-600">
              <p className="font-medium">This will permanently delete:</p>
              <ul className="list-disc ml-4 mt-1 text-xs space-y-0.5">
                {cleanupType === 'tenants' && (
                  <>
                    <li><strong>{selectedCleanupIds.size} tenant{selectedCleanupIds.size !== 1 ? 's' : ''}</strong> and ALL their data from both databases</li>
                    <li>All users, trips, expenses, subscriptions, configs, branding</li>
                    <li>Domain mappings, referral data, billing events, API keys</li>
                  </>
                )}
                {cleanupType === 'plans' && (
                  <>
                    <li><strong>{selectedCleanupIds.size} plan{selectedCleanupIds.size !== 1 ? 's' : ''}</strong> and their feature defaults</li>
                    <li>ALL tenants assigned to these plans (cascade delete)</li>
                    <li>All user data in those tenants from both databases</li>
                  </>
                )}
                {cleanupType === 'audit' && (
                  <li><strong>{selectedCleanupIds.size} audit log entr{selectedCleanupIds.size !== 1 ? 'ies' : 'y'}</strong> will be removed</li>
                )}
              </ul>
            </div>
          </div>

          {cleanupType !== 'audit' && (
            <p className="text-xs text-surface-500">
              Note: Stripe subscriptions are NOT automatically cancelled. Handle those manually.
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1.5">
              Type <strong className="text-red-500">DELETE</strong> to confirm
            </label>
            <input
              type="text"
              value={bulkDeleteConfirm}
              onChange={e => setBulkDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              className="input-field w-full py-2 text-sm"
            />
          </div>

          {bulkDeleteError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-600">
              Error: {bulkDeleteError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowBulkDeleteModal(false); setBulkDeleteConfirm(''); }}
              className="px-4 py-2 rounded-lg text-sm text-surface-600 hover:bg-surface-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting || bulkDeleteConfirm !== 'DELETE'}
              className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {bulkDeleting && <Loader2 size={14} className="animate-spin" />}
              Permanently Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Remove User Confirmation Modal ────────────────────────────────── */}
      <Modal
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title="Remove User"
        subtitle="This action permanently deletes all user data within this tenant."
      >
        {removeTarget && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-surface-100 text-sm space-y-1">
              <p><span className="text-surface-500">User:</span> <span className="font-medium text-surface-900">{removeTarget.full_name || removeTarget.email}</span></p>
              <p><span className="text-surface-500">Email:</span> <span className="font-medium text-surface-900">{removeTarget.email}</span></p>
              <p><span className="text-surface-500">Tenant:</span> <span className="font-medium text-surface-900">{selectedTenant?.name}</span></p>
            </div>
            <p className="text-sm text-surface-500">
              This will hard-delete all trips, expenses, subscriptions, notifications,
              and other data for this user in this tenant. The user's auth account remains intact
              and they can re-register.
            </p>
            {removeError && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-600">
                Error: {removeError}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setRemoveTarget(null)}
                className="px-4 py-2 rounded-lg text-sm text-surface-600 hover:bg-surface-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveUser}
                disabled={removing}
                className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {removing && <Loader2 size={14} className="animate-spin" />}
                Remove User
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Delete Tenant Confirmation Modal ──────────────────────────────── */}
      <Modal
        open={showDeleteTenant}
        onClose={() => { setShowDeleteTenant(false); setDeleteConfirmText(''); }}
        title="Delete Tenant"
        subtitle="This is an extremely destructive action."
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
            <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div className="text-sm text-red-600">
              <p className="font-medium">This will permanently delete:</p>
              <ul className="list-disc ml-4 mt-1 text-xs space-y-0.5">
                <li>All user data (trips, expenses, subscriptions) from App DB</li>
                <li>All tenant config (pricing plans, features) from App DB</li>
                <li>The tenant and ALL related records from Platform DB</li>
                <li>Branding, feature configs, domain mappings, referral data</li>
                <li>Billing events, API keys, announcements, bulletins</li>
              </ul>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1.5">
              Type <strong className="text-red-500">{selectedTenant?.name}</strong> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder={selectedTenant?.name}
              className="input-field w-full py-2 text-sm"
            />
          </div>

          <p className="text-xs text-surface-500">
            Note: Stripe Connect accounts and subscriptions are NOT automatically cancelled.
            Handle those manually if applicable.
          </p>

          {deleteError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-600">
              Error: {deleteError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowDeleteTenant(false); setDeleteConfirmText(''); }}
              className="px-4 py-2 rounded-lg text-sm text-surface-600 hover:bg-surface-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteTenant}
              disabled={deleting || deleteConfirmText !== selectedTenant?.name}
              className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {deleting && <Loader2 size={14} className="animate-spin" />}
              Permanently Delete Tenant
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Delete Plan Confirmation Modal ──────────────────────────────── */}
      <Modal
        open={showDeletePlan}
        onClose={() => { setShowDeletePlan(false); setPlanDeleteConfirm(''); }}
        title="Delete Plan"
        subtitle="This is an extremely destructive action."
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
            <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
            <div className="text-sm text-red-600">
              <p className="font-medium">This will permanently delete:</p>
              <ul className="list-disc ml-4 mt-1 text-xs space-y-0.5">
                <li>The plan &quot;{selectedPlan?.display_name}&quot; and all its feature defaults</li>
                {planTenantCount > 0 && (
                  <>
                    <li><strong>{planTenantCount} tenant{planTenantCount !== 1 ? 's' : ''}</strong> assigned to this plan</li>
                    <li>All user data (trips, expenses, subscriptions) in those tenants</li>
                    <li>All tenant configs (pricing plans, features, branding) from both DBs</li>
                    <li>Domain mappings, referral data, billing events, API keys</li>
                  </>
                )}
              </ul>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-700 mb-1.5">
              Type <strong className="text-red-500">{selectedPlan?.display_name}</strong> to confirm
            </label>
            <input
              type="text"
              value={planDeleteConfirm}
              onChange={e => setPlanDeleteConfirm(e.target.value)}
              placeholder={selectedPlan?.display_name}
              className="input-field w-full py-2 text-sm"
            />
          </div>

          <p className="text-xs text-surface-500">
            Note: Stripe subscriptions for affected tenants are NOT automatically cancelled.
            Handle those manually before deleting.
          </p>

          {deletePlanError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-600">
              Error: {deletePlanError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowDeletePlan(false); setPlanDeleteConfirm(''); }}
              className="px-4 py-2 rounded-lg text-sm text-surface-600 hover:bg-surface-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeletePlan}
              disabled={deletingPlan || planDeleteConfirm !== selectedPlan?.display_name}
              className="px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {deletingPlan && <Loader2 size={14} className="animate-spin" />}
              Permanently Delete Plan
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
